import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Panel, KpiGrid, KpiCard, Badge, Button } from '../../components/design';
import Icon from '../../components/Icon';

const CORE_API = '/api';
const SERVICES_PAGE_SIZE = 50;

export default function ServicesTab({ serverId, canEdit }) {
  const [svcFilter, setSvcFilter] = useState('');
  const [svcStatusFilter, setSvcStatusFilter] = useState('all');
  const [svcMonitoredFilter, setSvcMonitoredFilter] = useState('all');
  const [svcActionLoading, setSvcActionLoading] = useState(null);
  const [svcActionResult, setSvcActionResult] = useState(null);
  const [svcSortBy, setSvcSortBy] = useState('name');
  const [svcAutoRefresh, setSvcAutoRefresh] = useState(false);
  const [svcPage, setSvcPage] = useState(0);
  const [svcScanning, setSvcScanning] = useState(false);

  const { data: servicesData, isLoading: servicesLoading, error: servicesError, refetch: refetchServices } = useQuery({
    queryKey: ['servers', serverId, 'services', 'cached'],
    queryFn: () => fetch(`${CORE_API}/servers/${serverId}/services`).then(res => res.json()),
    refetchInterval: svcAutoRefresh ? 15000 : false,
  });

  const runSvcScan = async () => {
    setSvcScanning(true);
    try {
      await fetch(`${CORE_API}/servers/${serverId}/services?scan=true`);
      refetchServices();
    } catch (e) {
      console.error('Service scan failed:', e);
    }
    setSvcScanning(false);
  };

  const services = servicesData?.services || [];

  const svcFiltered = services
    .filter(s => {
      if (svcStatusFilter !== 'all' && s.active_state !== svcStatusFilter) return false;
      if (svcFilter && !s.service_name.toLowerCase().includes(svcFilter.toLowerCase()) &&
          !(s.description || '').toLowerCase().includes(svcFilter.toLowerCase())) return false;
      if (svcMonitoredFilter === 'monitored' && !s.monitored) return false;
      if (svcMonitoredFilter === 'unmonitored' && s.monitored) return false;
      return true;
    })
    .sort((a, b) => {
      if (svcSortBy === 'status') {
        const order = { active: 0, running: 0, failed: 1, inactive: 2, dead: 2, exited: 2 };
        return (order[a.active_state] ?? 3) - (order[b.active_state] ?? 3);
      }
      if (svcSortBy === 'type') return (a.service_type || '').localeCompare(b.service_type || '');
      return a.service_name.localeCompare(b.service_name);
    });
  const svcTotalFiltered = svcFiltered.length;
  const svcTotalPages = Math.ceil(svcTotalFiltered / SERVICES_PAGE_SIZE);
  const svcPaged = svcFiltered.slice(svcPage * SERVICES_PAGE_SIZE, (svcPage + 1) * SERVICES_PAGE_SIZE);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {services.length > 0 && (
        <KpiGrid>
          <KpiCard label="Total" value={services.length} countUp icon="server" iconBg="rgba(99,102,241,0.10)" iconColor="#6366f1" sub="discovered" />
          <KpiCard label="Running" value={services.filter(s => s.active_state === 'active' || s.active_state === 'running').length} countUp icon="circle-check" iconBg="rgba(52,211,153,0.10)" iconColor="#34d399" sub="active" />
          <KpiCard label="Stopped" value={services.filter(s => s.active_state === 'inactive' || s.active_state === 'dead' || s.active_state === 'exited').length} countUp icon="circle-pause" iconBg="rgba(251,191,36,0.10)" iconColor="#fbbf24" sub="inactive" />
          <KpiCard label="Failed" value={services.filter(s => s.active_state === 'failed').length} countUp icon="circle-x" iconBg="rgba(248,113,113,0.10)" iconColor="#f87171" sub="attention" />
          <KpiCard label="Monitored" value={services.filter(s => s.monitored).length} countUp icon="bell" iconBg="rgba(99,102,241,0.10)" iconColor="#6366f1" sub="with policy" />
        </KpiGrid>
      )}

      <Panel title="Services" sub={`${svcTotalFiltered} total`} noPad>
        <div className="filter-bar" style={{ margin: '16px 16px 0' }}>
          <input type="text" value={svcFilter} onChange={e => { setSvcFilter(e.target.value); setSvcPage(0); }}
            placeholder="Search services..." style={{ flex: '1 1 200px', maxWidth: 280 }} />
          <select value={svcStatusFilter} onChange={e => { setSvcStatusFilter(e.target.value); setSvcPage(0); }}>
            <option value="all">All states</option>
            <option value="active">Running</option>
            <option value="inactive">Stopped</option>
            <option value="failed">Failed</option>
            <option value="activating">Starting</option>
          </select>
          <select value={svcSortBy} onChange={e => setSvcSortBy(e.target.value)}>
            <option value="name">Name</option>
            <option value="status">Status</option>
            <option value="type">Type</option>
          </select>
          <select value={svcMonitoredFilter} onChange={e => { setSvcMonitoredFilter(e.target.value); setSvcPage(0); }}>
            <option value="all">All</option>
            <option value="monitored">Monitored</option>
            <option value="unmonitored">Unmonitored</option>
          </select>
          <Button variant={svcAutoRefresh ? 'primary' : 'ghost'} size="sm" icon="refresh-cw" onClick={() => setSvcAutoRefresh(p => !p)}>Auto</Button>
          <Button variant="primary" size="sm" icon="scan-search" onClick={runSvcScan} disabled={svcScanning}>
            {svcScanning ? 'Scanning...' : 'Scan'}
          </Button>
        </div>

        {servicesData?.cached && !svcScanning && services.length > 0 && (
          <div className="muted" style={{ margin: '8px 16px 0', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icon name="info" size={12} />
            Cached data — click <strong>Scan</strong> for live SSH refresh
          </div>
        )}

        {svcActionResult && (
          <div style={{ margin: '12px 16px 0', padding: '10px 14px', borderRadius: 10, fontSize: 12, fontFamily: 'var(--mono)', background: svcActionResult.error ? 'rgba(248,113,113,0.10)' : 'rgba(52,211,153,0.10)', color: svcActionResult.error ? 'var(--danger)' : 'var(--success)', maxHeight: 120, overflow: 'auto' }}>
            <div className="flex between center">
              <span style={{ fontWeight: 600 }}>{svcActionResult.action || 'Result'}</span>
              <button onClick={() => setSvcActionResult(null)} style={{ cursor: 'pointer', background: 'none', border: 'none', color: 'inherit' }}><Icon name="x" size={13} /></button>
            </div>
            <pre style={{ whiteSpace: 'pre-wrap', margin: '6px 0 0' }}>{svcActionResult.output || svcActionResult.error || JSON.stringify(svcActionResult)}</pre>
          </div>
        )}

        {servicesLoading ? (
          <div className="muted" style={{ textAlign: 'center', padding: '40px 0' }}>Loading services...</div>
        ) : servicesError ? (
          <div className="empty">
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--danger)', marginBottom: 6 }}>{servicesError.message}</h3>
            <Button variant="danger" size="sm" onClick={() => refetchServices()}>Retry</Button>
          </div>
        ) : services.length === 0 ? (
          <div className="empty">
            <div style={{ fontSize: 40, marginBottom: 10 }}>🔍</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>No services discovered yet.</h3>
            <p className="muted">SSH connection is required. Click <strong>Scan</strong> to discover services.</p>
          </div>
        ) : (
          <div style={{ padding: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 10 }}>
              {svcPaged.map((svc, idx) => {
                const isActive = svc.active_state === 'active' || svc.active_state === 'running';
                const isFailed = svc.active_state === 'failed';
                const loading = svcActionLoading === svc.service_name;
                const hasActions = svc.service_type === 'systemd';
                const memMb = svc.memory_bytes ? (svc.memory_bytes / 1024 / 1024).toFixed(1) : null;

                return (
                  <div key={svc.service_name || idx}
                    style={{
                      padding: 14, borderRadius: 12, background: 'var(--glass)',
                      border: isFailed ? '1px solid var(--danger)' : isActive ? '1px solid var(--success)' : '1px solid var(--glass-border)',
                    }}>
                    <div className="flex items-start justify-between gap-2">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="flex items-center gap-1.5">
                          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 9999, flexShrink: 0, background: isActive ? 'var(--success)' : isFailed ? 'var(--danger)' : 'var(--text-3)' }} />
                          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {svc.service_name}
                          </span>
                          {svc.enabled === true && (
                            <Icon name="check-circle" size={12} style={{ color: 'var(--success)', flexShrink: 0 }} />
                          )}
                          {svc.enabled === false && (
                            <Icon name="ban" size={12} style={{ color: 'var(--text-3)', flexShrink: 0 }} />
                          )}
                        </div>
                        {svc.description && (
                          <p className="muted" style={{ fontSize: 11, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{svc.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={async () => {
                          try {
                            await fetch(`${CORE_API}/servers/${serverId}/services/${svc.service_name}/monitor?monitored=${!svc.monitored}`, { method: 'POST' });
                            refetchServices();
                          } catch (e) {}
                        }} disabled={loading}
                          style={{ padding: 4, background: 'none', border: 'none', cursor: 'pointer' }}
                          title={svc.monitored ? 'Monitored - click to stop' : 'Click to monitor'}>
                          <Icon name="bell" size={15} style={{ color: svc.monitored ? 'var(--accent-1)' : 'var(--text-3)' }} />
                        </button>
                        <Badge tone={isActive ? 'green' : isFailed ? 'red' : 'gray'}>{svc.sub_state || svc.active_state || 'unknown'}</Badge>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-x-3 gap-y-0.5" style={{ marginTop: 8, fontSize: 10 }}>
                      {svc.pid && (
                        <span className="muted">PID: <span className="mono" style={{ color: 'var(--text-2)' }}>{svc.pid}</span></span>
                      )}
                      {svc.cpu_percent != null && (
                        <span className="muted">CPU: <span className="mono" style={{ color: (svc.cpu_percent || 0) > 50 ? 'var(--danger)' : 'var(--text-2)' }}>{Number(svc.cpu_percent).toFixed(1)}%</span></span>
                      )}
                      {svc.mem_percent != null && (
                        <span className="muted">MEM: <span className="mono" style={{ color: 'var(--text-2)' }}>{Number(svc.mem_percent).toFixed(1)}%</span></span>
                      )}
                      {memMb !== null && (
                        <span className="muted">Mem: <span className="mono" style={{ color: 'var(--text-2)' }}>{memMb} MB</span></span>
                      )}
                      <span className="muted">Type: <span style={{ color: 'var(--text-2)' }}>{svc.service_type}</span></span>
                      {svc.load_state && (
                        <span className="muted">Load: <span className="mono" style={{ color: svc.load_state === 'loaded' ? 'var(--success)' : 'var(--danger)' }}>{svc.load_state}</span></span>
                      )}
                    </div>

                    {canEdit && hasActions && (
                      <div className="flex flex-wrap gap-1.5" style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--glass-border)' }}>
                        {isActive ? (
                          <>
                            <Button variant="ghost" size="sm" onClick={async () => {
                              setSvcActionLoading(svc.service_name);
                              try {
                                const res = await fetch(`${CORE_API}/servers/${serverId}/services/${svc.service_name}/restart`, { method: 'POST' });
                                const data = await res.json();
                                setSvcActionResult(data);
                                refetchServices();
                              } catch (e) { setSvcActionResult({ error: e.message }); }
                              setSvcActionLoading(null);
                            }} disabled={loading}>
                              {loading ? '...' : 'Restart'}
                            </Button>
                            <Button variant="danger" size="sm" onClick={async () => {
                              setSvcActionLoading(svc.service_name);
                              try {
                                const res = await fetch(`${CORE_API}/servers/${serverId}/services/${svc.service_name}/stop`, { method: 'POST' });
                                const data = await res.json();
                                setSvcActionResult(data);
                                refetchServices();
                              } catch (e) { setSvcActionResult({ error: e.message }); }
                              setSvcActionLoading(null);
                            }} disabled={loading}>
                              {loading ? '...' : 'Stop'}
                            </Button>
                          </>
                        ) : (
                          <Button variant="primary" size="sm" onClick={async () => {
                            setSvcActionLoading(svc.service_name);
                            try {
                              const res = await fetch(`${CORE_API}/servers/${serverId}/services/${svc.service_name}/start`, { method: 'POST' });
                              const data = await res.json();
                              setSvcActionResult(data);
                              refetchServices();
                            } catch (e) { setSvcActionResult({ error: e.message }); }
                            setSvcActionLoading(null);
                          }} disabled={loading}>
                            {loading ? '...' : 'Start'}
                          </Button>
                        )}
                        {svc.enabled === true ? (
                          <Button variant="ghost" size="sm" onClick={async () => {
                            setSvcActionLoading(svc.service_name);
                            try {
                              const res = await fetch(`${CORE_API}/servers/${serverId}/services/${svc.service_name}/disable`, { method: 'POST' });
                              const data = await res.json();
                              setSvcActionResult(data);
                              refetchServices();
                            } catch (e) { setSvcActionResult({ error: e.message }); }
                            setSvcActionLoading(null);
                          }} disabled={loading}
                            title="Disable at boot">
                            {loading ? '...' : 'Disable'}
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={async () => {
                            setSvcActionLoading(svc.service_name);
                            try {
                              const res = await fetch(`${CORE_API}/servers/${serverId}/services/${svc.service_name}/enable`, { method: 'POST' });
                              const data = await res.json();
                              setSvcActionResult(data);
                              refetchServices();
                            } catch (e) { setSvcActionResult({ error: e.message }); }
                            setSvcActionLoading(null);
                          }} disabled={loading}
                            title="Enable at boot">
                            {loading ? '...' : 'Enable'}
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={async () => {
                          setSvcActionLoading(svc.service_name);
                          try {
                            const res = await fetch(`${CORE_API}/servers/${serverId}/services/${svc.service_name}/status`);
                            const data = await res.json();
                            setSvcActionResult(data);
                          } catch (e) { setSvcActionResult({ error: e.message }); }
                          setSvcActionLoading(null);
                        }} disabled={loading}>
                          {loading ? '...' : 'Info'}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {svcTotalPages > 1 && (
              <div className="flex items-center justify-between" style={{ paddingTop: 12 }}>
                <span className="muted" style={{ fontSize: 12 }}>
                  Showing {Math.min(svcPage * SERVICES_PAGE_SIZE + 1, svcTotalFiltered)}-{Math.min((svcPage + 1) * SERVICES_PAGE_SIZE, svcTotalFiltered)} of {svcTotalFiltered}
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setSvcPage(p => Math.max(0, p - 1))} disabled={svcPage === 0}
                    className="chip" style={{ opacity: svcPage === 0 ? 0.3 : 1 }}>
                    Prev
                  </button>
                  {Array.from({ length: Math.min(svcTotalPages, 7) }, (_, i) => {
                    let pageNum;
                    if (svcTotalPages <= 7) {
                      pageNum = i;
                    } else if (svcPage < 4) {
                      pageNum = i;
                    } else if (svcPage > svcTotalPages - 5) {
                      pageNum = svcTotalPages - 7 + i;
                    } else {
                      pageNum = svcPage - 3 + i;
                    }
                    return (
                      <button key={pageNum} onClick={() => setSvcPage(pageNum)}
                        className="chip" style={svcPage === pageNum ? { borderColor: 'var(--accent-1)', color: 'var(--accent-1)' } : undefined}>
                        {pageNum + 1}
                      </button>
                    );
                  })}
                  <button onClick={() => setSvcPage(p => Math.min(svcTotalPages - 1, p + 1))} disabled={svcPage >= svcTotalPages - 1}
                    className="chip" style={{ opacity: svcPage >= svcTotalPages - 1 ? 0.3 : 1 }}>
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Panel>
    </div>
  );
}
